import { UserRole } from '@/@types/graphql-types';
import { STORAGE_MIGRATION_REVISION } from '@/migrate-local-storage';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import {
	DiscoverySettings,
	BuyBoxFilterOptions,
	NewBuyBoxView,
	DEFAULT_USERSETTINGS,
	VisibleFlyoutType,
	UserInfo,
	ProfileInfo,
	premiumPurchaseFlowState as PremiumPurchaseFlowState,
	UserSearches,
	UserSettings,
	VisibleFlyouts,
	UserState,
	EMAIL_HASH_KEY,
	Consent,
	UpdateUserInfoPayload,
	UserExperimentsInfo,
} from '../useUserStoreTypes';
import { LoginProvider, LoginProviderAccount, LoginProviderByFirebaseProviderId } from '@/enums/login';
import { OnboardingStatus } from '@/enums/onboarding-status';
import { WebLocale } from '@/enums/web-locale';
import { OfferPresentationType } from '@/interfaces/titles';
import { SubscriptionInfo } from '@/interfaces/subscriptions';
import { experimentConfig, isBackendExperiment, ValidExperimentName } from '@/libraries/experiment';

import { LocalDataProcessingServices } from '@/helpers/usercentrics-helper';
import { DataProcessingService, type DataProcessingServiceConsent } from '@/libraries/usercentrics/types';
import { useLanguageStore } from './useLanguageStore';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { hasAnyConsentsGiven } from '@/helpers/consent-helper';
import { toUTCDateOnly } from '@/helpers/date-helper';
import { objectKeys } from '@/helpers/object-helper';
import type { ApolloProvider } from 'vue-apollo';
import { getIdServiceVariables } from '@/helpers/id-helper';
import {
	GetNewDeviceIdInputDocument,
	GetNewDeviceIdInputMutation,
	GetNewDeviceIdInputMutationVariables,
} from '@/graphql/mutation/GetNewDeviceId.mutation';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { currentUser, generateToken } from '@/helpers/user-helper';
import { deleteUser, getAuth, sendEmailVerification, signOut } from 'firebase/auth';
import { useUserStats } from '@/helpers/composables/useUserStats';
import { findSubscriptionBy } from '@/helpers/pro-web-helper';
import { getSettings, mutateSettings } from '@/helpers/settings-helper';
import { getOnboardingStatus } from '@/helpers/onboarding-helper';
import { getAuthHeaders, getAxios } from '@/helpers/axios-helper';
import type { AuthResponse } from '@/interfaces/responses/auth';
import { generateHashedEmail } from '@/helpers/uuid-helper';
import { saveConsents as saveConsentsHelper } from '@/helpers/cookiebot-helper';
import { cookieStorage } from '@/stores/cookieStorage';
import { STORE_NAMESPACE } from '@/piniaStore';
import { storeTCString } from '@/helpers/impression-tracker-helper';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { until } from '@vueuse/core';
import { iapCountries, updatePremiumStatus } from '@/helpers/iap-helper';

const defaultSubscriptionInfo: SubscriptionInfo = {
	id: '',
	type: 'regular',
	timeframe: 'monthly',
	prices: {
		DEFAULT: {
			price: 2.49,
			currency: 'EUR',
			priceId: '',
		},
	},
};

function removeEmailHash() {
	if (!process.client) return;

	localStorage.removeItem(EMAIL_HASH_KEY);
}

// store hashed email for snowplow flex-context event on title pages
async function saveEmailHash(email: string) {
	if (!process.client) return;

	const hash = await generateHashedEmail(email);
	localStorage.setItem(EMAIL_HASH_KEY, JSON.stringify(hash));
}
const RECENT_SEARCH_TERMS_COUNT = 10;

export const useUserStore = defineStore(
	'user',
	() => {
		const languageStore = useLanguageStore();
		const routingStore = useRoutingStore();

		const hasLoadedSettings = ref(false);

		//State
		const accessToken = ref<string | undefined>(undefined); // used to send for certain routes with authorization token

		const discoverySettings = ref<DiscoverySettings>({ continueWatchingHintDismissed: false } as DiscoverySettings);
		const enableProvidersFilter = ref<boolean>(false);
		const hasCancelledPremium = ref<boolean>(false);
		const hideSeenDislikedTitles = ref<boolean>(false);
		const info = ref<UserInfo>({});
		const isPremium = ref<boolean>(false);
		const profileIsComplete = ref<boolean>(false);
		const isVerificationEmailSent = ref<boolean>(false);
		const jwId = ref<string | undefined>(undefined);
		const jwLoginId = ref<string | undefined>(undefined);
		const lastUsedLoginProvider = ref<string | undefined>(undefined);
		const loggedInProviders = ref<LoginProvider[]>([]); // list of provider names the user is authenticated with
		const loginProgress = ref<boolean>(false);
		const onboardingStatus = ref<OnboardingStatus | null>(null);
		const persistedOverriddenLanguage = ref<string | null>(null);
		const persistedWebLocale = ref<WebLocale | null>(null);
		const permanentAudiencesSubgenres = ref<string[]>([] as string[]);

		const experimentsLoaded = ref(false);
		const preferredExperiments = ref<{ [experiment: string]: { variant: string; sendSnowplowEvent: boolean } }>({});
		const preferences = ref<{ buyboxPresentationType: OfferPresentationType | null }>({
			buyboxPresentationType: null,
		});
		const profileInfo = ref<ProfileInfo | null>(null);

		const newBuyboxFilterType = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);
		const buyboxFilterTypeOptimistic = ref<null | BuyBoxFilterOptions | OfferPresentationType>(null);

		function setBuyboxFilterTypeOptimistic(filterType: null | BuyBoxFilterOptions | OfferPresentationType) {
			buyboxFilterTypeOptimistic.value = filterType;
		}

		const newBuyboxView = ref<NewBuyBoxView>(NewBuyBoxView.LIST);
		const newBuyboxTooltipAcknowledged = ref<boolean>(false);
		// Temp: until we fully remove the old BB from the titlePage.
		const loggedInNewBuyBoxView = ref<NewBuyBoxView | null>(null);
		const displayHomeSportsModule = ref<boolean>(true);
		const premiumPurchaseFlowState = ref<PremiumPurchaseFlowState | null>(null);
		const searches = ref<UserSearches>({ recentSearchTerms: [], recentlyVisitedTitleIds: [] });
		const seenSurveys = ref<Record<string, string[]>>({});
		const settings = ref<UserSettings>(DEFAULT_USERSETTINGS); // user settings for non- and logged-in users
		const settingsProvider = ref<UserSettings['providers']>(DEFAULT_USERSETTINGS.providers); // only changes when settings.providers really have changed
		const storageMigratedRevision = ref<number>(STORAGE_MIGRATION_REVISION); // used to determine when to do migration upon startup
		const lastTimeMobileAppBannerWasClosedTS = ref<number>(0); // use to determine when mobile app banner was closed last time
		const lastTimeListsOnboardingWasShownTS = ref<number>(0); // use to determine when onboarding was shown last time
		// Start COOKIE_BANNER_EXPERIMENT
		const subscriptionInfoState = ref<SubscriptionInfo | undefined>();
		const timeZone = ref<string | undefined>(undefined);
		const visibleFlyouts = ref<VisibleFlyouts>({
			[VisibleFlyoutType.HOME_INVITATION_BOX]: true,
			[VisibleFlyoutType.ONBOARDED_HOME]: true as boolean,
			[VisibleFlyoutType.FREEMIUM_BANNER]: true,
		});
		// we need a way to reactively store all the DPS (data processing services) from Usercentrics.
		// then we can build an eligible DPS list by combining Usercentrics DPS and our usercentrics-helper.ts DPS.
		// with this we won't break any web app releases by changing them.
		const usercentricsDPS = ref<{ id: string; name: string }[] | null>(null);
		const userRole = ref<UserRole>(UserRole.User);

		const isUserFirstVisit = ref<boolean | null>(true); // true: default value, null: on first visit, false: user has visited before
		const showOldTitlePageState = ref<boolean>(false); // used to determine if we should show the old title page or not
		// contains all the experiments that the user was naturally acquired to, we get this from the BE.
		const acquiredExperiments = ref<Partial<{ [key in ValidExperimentName<true>]: string | null }>>({});
		// contains all the preferred experiments that were set by the user using query params (?preferred_variant=[experiment-name]:[experiment-variant])
		const _preferredExperiments = ref<Partial<{ [key in ValidExperimentName<true>]: string | null }>>({});
		// contains all the experiments that were activated for the user ["experimentName:variantName"]
		const activatedExperiments = ref<string[]>([]);
		// stores the TCF string from UserCentrics
		const uc_tcf = ref<string | null>(null);

		const accountCreationTime = ref<Date | null>(null); // used to be set in the user cookie instead of the whole info object.
		// used to determine if the user has opted out of consents
		const optedOutConsent = ref(false);

		// OLD BUYBOX EXP
		const buyboxExpView = ref(NewBuyBoxView.GRID);

		function setBuyboxExpView(viewType: NewBuyBoxView) {
			buyboxExpView.value = viewType;
			// Update Sentry tag
			import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
				updateSentryTags({ buybox_exp_view: viewType });
			});
		}

		const buyboxViewExpTooltipAcknowledged = ref(false);
		function setBuyboxViewExpTooltipAcknowledged() {
			buyboxViewExpTooltipAcknowledged.value = true;
		}
		// OLD BUYBOX EXP

		//Getters
		const userInfo = computed(() => {
			return !accessToken.value ? {} : info.value;
		});
		const buyboxPresentationType = computed(() => {
			return preferences.value.buyboxPresentationType;
		});
		const isLoggedIn = computed(() => {
			return loggedInProviders.value.length > 0;
		});
		const newBuyboxTooltipAcknowledgedGetter = computed(() => {
			return newBuyboxTooltipAcknowledged.value;
		});

		function isFlyoutVisible(name: string) {
			return visibleFlyouts.value[`${name}`];
		}

		const bestEffortId = computed(() => {
			return jwLoginId.value || jwId.value;
		});
		const consents = computed(() => {
			// TODO Store should produce computed consents. Right now we assume some consents could be missing (see ConsentModal.vue)
			return settings.value?.jw_consents || {};
		});
		const isAccountVerified = computed(() => {
			const isAuthenticatedWithEmail = userInfo?.value.loginProvider === LoginProvider.JUSTWATCH;
			return isLoggedIn.value && (isAuthenticatedWithEmail ? !!info.value?.emailVerified : true);
		});
		const dpsConsents = computed(() => {
			const explicitlyConsented = (settings.value?.uc_consents || [])
				.filter(item => item.consent)
				.map(item => item.id);
			const excplicitlyRejected = (settings.value?.uc_consents || [])
				.filter(item => !item.consent)
				.map(item => item.id);

			return (Object.keys(LocalDataProcessingServices) as DataProcessingService[]).reduce((root, serviceKey) => {
				const service = LocalDataProcessingServices[serviceKey];
				let consentValue = null;
				if (explicitlyConsented.includes(service.id)) {
					// user HAS consented to this service
					consentValue = true;
				} else if (excplicitlyRejected.includes(service.id)) {
					// user HAS rejected consent to this service
					consentValue = false;
				} else {
					// user HAS NOT consented nor rejected to this service
					consentValue = null;
				}
				root![serviceKey] = consentValue;
				return root;
			}, {} as Record<DataProcessingService, boolean | null> | null);
		});

		// In cases like 'injecting external trackers' we need to have the
		// user's full consent to ALL partners that we currently have set up as DPS.
		// So we can be as data privacy compliant as possible.
		//
		// We differentiate between two groups:
		// - UC countries: all partner consents must have `consent: true`
		// - non-UC countries: the `jw_consents.allother` value must be `true`
		const strictlyAllConsentsGranted = computed(() => {
			if (hasUCConsentBanner(languageStore.webLocale)) {
				const uc_consents = settings.value?.uc_consents;
				const totalDPS = usercentricsDPS.value ? usercentricsDPS.value.length : 0;
				// totalDPS is synced with UserCentrics, so it will be 0 in the beginning.
				// make sure they are loaded and not empty at 0.
				return totalDPS > 0 && usercentricsDPS.value?.length === uc_consents?.length;
			} else {
				return hasAnyConsentsGiven(consents.value);
			}
		});

		const userSearches = computed(() => {
			return {
				recentSearchTerms: (searches.value.recentSearchTerms ?? []).slice(0, 10),
				recentlyVisitedTitleIds: (searches.value.recentlyVisitedTitleIds ?? []).slice(0, 10),
			};
		});

		const isUserOnboarded = computed(() => {
			const status = [
				OnboardingStatus.ONBOARDED,
				OnboardingStatus.USER,
				OnboardingStatus.GLOBAL_CLICKED_CTA,
				OnboardingStatus.SIGNED_UP,
			];
			return onboardingStatus.value != null && status.includes(onboardingStatus.value);
		});

		const isHideSeenDislikedTitlesEnabled = computed(() => {
			return hideSeenDislikedTitles.value;
		});
		const isAdmin = computed(() => {
			return userRole.value === UserRole.Admin;
		});
		const isEditor = computed(() => {
			return userRole.value === UserRole.Editor;
		});

		const isProUser = computed(() => {
			return userRole.value === UserRole.Pro || isPremium.value;
		});

		const subscriptionInfo = computed(() => {
			return subscriptionInfoState.value || defaultSubscriptionInfo;
		});
		// TITLE_DETAIL_SHAOW_PAGE_LOGGED_IN_EXP
		const loggedInNewBuyboxView = computed(() => {
			if (isLoggedIn.value) {
				const defaultView = languageStore.webLocale === WebLocale.uk ? NewBuyBoxView.LIST : NewBuyBoxView.GRID;
				// ADDED THIS FOR COMPATIBILITY WITH OLD USER COOKIES (as they don't have the newly created accountCreationTime)
				const creationTime = accountCreationTime.value ?? info.value?.creationTime;

				// this one might be temporary but we wanna only show by default the new buybox view to users that signed up after a 2025-05-20
				if (loggedInNewBuyBoxView.value == null && creationTime) {
					// converting dates to UCT to prevent any hydration errors.
					const creationDate = toUTCDateOnly(new Date(creationTime));
					const comparisonDate = new Date(Date.UTC(2025, 4, 20));
					if (creationDate >= comparisonDate) {
						return NewBuyBoxView.LIST;
					}

					return defaultView;
				}
				return loggedInNewBuyBoxView.value ?? defaultView;
			}
			return newBuyboxView.value;
		});

		// TITLE_DETAIL_SHAOW_PAGE_LOGGED_IN_EXP
		const showOldTitlePage = computed(() => {
			if (!isLoggedIn.value) {
				return false;
			}
			return showOldTitlePageState.value;
		});

		const userExperimentsInfo = computed<UserExperimentsInfo>(() => {
			const activeVariants: UserExperimentsInfo = {};
			for (const experimentName of objectKeys(acquiredExperiments.value)) {
				const activeVariant =
					experimentName in _preferredExperiments.value
						? _preferredExperiments.value[experimentName]
						: acquiredExperiments.value[experimentName];

				if (activeVariant) {
					activeVariants[experimentName] = {
						variant: activeVariant,
						preferred: experimentName in _preferredExperiments.value,
						activated:
							activatedExperiments.value.includes(experimentName + ':' + activeVariant) ||
							isBackendExperiment(experimentName),
						affiliationShortId:
							experimentConfig?.[experimentName as ValidExperimentName]?.affiliationShortId || null,
					};
				}
			}
			return activeVariants;
		});

		const tcString = computed(() => {
			return uc_tcf.value;
		});

		const showFreemium = computed(() => {
			// show for premium-enabled users, no matter which country is set and for all platforms
			if (isPremium.value) return true;

			// show for supported countries on mobile (individual features are responsible for locked/unlocked state)
			const appCountry = languageStore.country;
			if (iapCountries.includes(appCountry)) return true;

			// hide in all other cases
			return false;
		});

		// ACTIONS
		function setAcquiredExperiments(experiments: UserState['acquiredExperiments']) {
			acquiredExperiments.value = experiments;
		}

		function setNewPreferredExperiments(preferredExperiments: UserState['_preferredExperiments']) {
			_preferredExperiments.value = preferredExperiments;
		}

		function setActivatedExperiments(experiments: string[]) {
			activatedExperiments.value = experiments;
		}

		function registerActivatedExperiment(experimentName: string) {
			const experimentVariant = userExperimentsInfo.value?.[experimentName]?.variant;
			const activeExperiment = experimentName + ':' + experimentVariant;

			if (experimentVariant && experimentName && !activatedExperiments.value.includes(activeExperiment)) {
				addActivatedExperiment(activeExperiment);
			}
		}

		function addActivatedExperiment(experimentToActivate: string) {
			if (!activatedExperiments.value.includes(experimentToActivate)) {
				activatedExperiments.value.push(experimentToActivate);
			}
		}

		async function fetchJwId(apolloProvider: ApolloProvider) {
			try {
				const variables = await getIdServiceVariables(jwId.value || null, settings.value?.jw_consents || {});

				const results = await apolloProvider.defaultClient.mutate<
					GetNewDeviceIdInputMutation,
					GetNewDeviceIdInputMutationVariables
				>({
					mutation: GetNewDeviceIdInputDocument,
					variables: {
						input: {
							doNotTrack: variables.dnt,
							ids: [variables.ids],
						},
					},
				});

				if (results?.data?.getNewDeviceId.deviceId) {
					setJwId(results?.data?.getNewDeviceId.deviceId);
				} else {
					TrackingHelper.trackEvent('idservice', {
						action: 'lookup',
						label: 'failed',
						nonInteraction: true,
					});
					throw 'no valid ID received';
				}
			} catch (error: any) {
				// Webapp can init, but better keep tracking as this is still a limited app experience (no settings)
				const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
				captureMessageForSentry('User Init Issue:', { error, where: '[user.store.ts] fetchJwId' }, 'error');
			}
		}

		function setJwId(id: string) {
			jwId.value = id;
		}

		function setBuyboxPresentationType(payload: {
			presentationType: OfferPresentationType | null;
			additionalContexts?: SnowplowContext[];
		}) {
			const { presentationType, additionalContexts = [] } = payload;

			preferences.value.buyboxPresentationType = presentationType;
			TrackingHelper.trackEvent(
				'presentationtype_change',
				{ action: presentationType ?? undefined },
				additionalContexts
			);

			// Update Sentry tag
			import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
				updateSentryTags({
					buybox_presentation_type: presentationType === null ? 'best' : presentationType,
				});
			});
		}

		function setNewBuyboxFilterType(filterType: BuyBoxFilterOptions) {
			newBuyboxFilterType.value = filterType;

			// Update Sentry tag
			import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
				updateSentryTags({ new_buybox_filter_type: filterType });
			});
			// TrackingHelper.trackEvent('presentationtype_change', { action: filterType ?? undefined }, additionalContexts);
			// fail-safe that the optimistic value is not out of sync
			setBuyboxFilterTypeOptimistic(filterType);
		}

		function setNewBuyboxView(viewType: NewBuyBoxView) {
			if (isLoggedIn.value) {
				loggedInNewBuyBoxView.value = viewType;
			}
			newBuyboxView.value = viewType;
			// Update Sentry tag
			import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
				updateSentryTags({ buybox_view: viewType });
			});
		}

		function setNewBuyboxTooltipAcknowledged() {
			newBuyboxTooltipAcknowledged.value = true;

			// Update Sentry tag
			import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
				updateSentryTags({ buybox_tooltip_acknowledged: true });
			});
		}

		function hideHomeSportsModule() {
			displayHomeSportsModule.value = false;
		}

		function setProfileIsComplete(isComplete: boolean) {
			profileIsComplete.value = isComplete;
		}
		/**
		 * Finds out if is login or signup
		 *
		 * @throws Error when user is not signed up yet
		 */
		async function verifyRegistration() {
			const user = await currentUser();

			if (!user) return;
			const tokenResult = await user.getIdTokenResult();

			if (tokenResult.claims.registration_error)
				throw `registration error: ${tokenResult.claims.registration_error}`;
			if (!tokenResult.claims.jw_login_id) throw 'not yet registered internally';
		}

		async function refreshToken() {
			const { captureMessageForSentry } = await import('@/helpers/sentry-helper');

			const maxRetries = 2;
			let attempt = 0;
			let lastError: Error | null = null;
			let token: string | undefined;

			// Retry logic to handle transient failures (network blips, temporary service issues)
			while (attempt <= maxRetries && !token) {
				lastError = null;

				try {
					token = await generateToken(accessToken.value, jwId.value);

					if (!token) {
						throw new Error('Error getting refresh token');
					}

					setAuthToken(token);
					return token;
				} catch (error: any) {
					lastError = error;

					captureMessageForSentry(
						`[user.store.ts] refreshToken attempt ${attempt + 1}/${maxRetries + 1} failed`,
						{ error, where: 'user.store.ts refreshToken' },
						attempt >= maxRetries ? 'error' : 'warning'
					);
				}

				attempt++;

				// Wait before retrying (exponential backoff: 500ms, 1000ms, 2000ms)
				if (attempt <= maxRetries && !token) {
					await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, attempt - 1)));
				}
			}

			// All retries failed - determine if we should logout
			// Only keep user logged in if jwLoginId exists AND this is likely a transient Firebase issue
			// (not an actually invalid token)
			if (!token && jwLoginId.value) {
				captureMessageForSentry(
					'[user.store.ts] refreshToken failed after retries but jwLoginId exists - logging out to prevent broken state',
					{
						lastError,
						jwLoginId: jwLoginId.value,
						attempts: maxRetries + 1,
						where: 'user.store.ts refreshToken',
					},
					'error'
				);
			}

			// Always logout after retries fail to prevent broken state
			// Even if jwLoginId exists, an invalid token means user can't make authenticated requests
			if (!token) {
				try {
					await logout();
				} catch (error: any) {
					captureMessageForSentry(
						'[user.store.ts] refreshToken: Error logging out after failed retries',
						{ error, where: 'user.store.ts refreshToken' },
						'error'
					);
				}
			}

			return token;
		}
		/**
		 * Logout for all providers
		 */
		async function logout(trackLogout = false) {
			// Call signout to register in the consumer api that the user has signed out
			if (trackLogout) {
				if (!accessToken.value && !jwId.value) {
					console.warn('Trying to logout without jwId');
					return;
				}

				const headers = await getAuthHeaders(accessToken.value, { ['DEVICE-ID']: jwId.value });
				await getAxios().post(`${JW_CONFIG.IDENTITY_URL}/sign_out`, {}, { headers });
			}

			try {
				await signOut(getAuth());
			} catch (err) {
				console.error('[Firebase] Signout error: ', err);
			}
			jwLoginId.value = undefined;
			accessToken.value = undefined;
			info.value = {};
			const lastLoginProvider: string = loggedInProviders.value[0] || 'justwatch';
			lastUsedLoginProvider.value = lastLoginProvider;
			loggedInProviders.value = [];

			try {
				useUserStats().refetchUserStats();
			} catch (err) {
				import('@/helpers/sentry-helper').then(({ captureMessageForSentry }) =>
					captureMessageForSentry(
						'[user.store.ts] LOGOUT: Error refetching user stats:',
						{ err, where: 'user.store.ts' },
						'error'
					)
				);
			}

			await setPremiumStatus(false);
			setProfileInfo(null);
			setProfileIsComplete(false);
			setSubscriptionInfo(null);
			await loadSettings();

			removeEmailHash();
			removeTCString();
			const locale = languageStore.webLocale;
			const newOnboardingStatus = getOnboardingStatus(locale, false, routingStore.activeRoute?.path!);
			// To reset onboarding status
			// Need to check if is loggedIn to avoid onboardingStatus reset
			// in case of failed login attempt (calls logout in that case)
			// update settings

			onboardingStatus.value = newOnboardingStatus;

			const constantStore = useConstantStore();
			await saveSettings({ providers: constantStore.preselectedProviders });
		}

		async function saveSettings(partialSettings: Partial<UserSettings> = {}) {
			const locale = languageStore.locale;
			const localSettings = await mutateSettings(
				jwId.value,
				accessToken.value,
				locale,
				settings.value,
				partialSettings
			);
			setSettings(localSettings);
		}

		async function loadSettings() {
			await until(jwId).toBeTruthy({ timeout: 500 });
			if (!jwId.value) return false;

			const token = accessToken.value;
			const locale = languageStore.locale;
			const settings = await getSettings(jwId.value, locale, token);

			setSettings(settings);
			hasLoadedSettings.value = true;
		}

		function setSettings(userSettings: UserSettings) {
			settings.value = userSettings;
			// make sure we only update setting's providers if they change
			const provider = userSettings.providers || [];
			if (provider && JSON.stringify(settingsProvider.value) !== JSON.stringify(provider)) {
				settingsProvider.value = provider;
			}
		}

		function setOptedOutConsent(optedOut: boolean) {
			optedOutConsent.value = optedOut;
		}

		function setSubscriptionInfo(info: string | null) {
			const foundSubscriptionInfo = findSubscriptionBy('id', info);
			subscriptionInfoState.value = foundSubscriptionInfo;
		}

		function setProfileInfo(info: ProfileInfo | null) {
			profileInfo.value = info;
		}

		async function setPremiumStatus(value: boolean) {
			if (!value && isPremium.value) {
				const filterStore = useFilterStore();
				await filterStore.resetPremiumFilters();

				setHideSeenDislikedTitles(false);
			}
			isPremium.value = value;
		}

		function setHideSeenDislikedTitles(value: boolean) {
			hideSeenDislikedTitles.value = value;
		}

		function setAuthToken(token: string) {
			accessToken.value = token;
		}

		function removeTCString() {
			if (!process.client) return;

			sessionStorage.removeItem('tcString');
		}

		async function registerInternally() {
			if (isLoggedIn.value) return;
			setLoginProgress(true);

			const user = await currentUser();

			if (!user) {
				throw new Error('[Firebase] internal registration failed: user not found');
			}

			// Get the firebase token, at this point we don't have the jw_login_id as part of claims
			const tokenResult = await user.getIdTokenResult(true);
			if (!tokenResult) {
				throw new Error('internal registration failed');
			}
			const { token } = tokenResult;

			try {
				await getAxios().post(
					`${JW_CONFIG.IDENTITY_URL}/register`,
					{},
					{
						headers: {
							Authorization: 'Bearer ' + token,

							['DEVICE-ID']: jwId.value!,
						},
					}
				);

				// After registration, get the token result again, this should now contain the jw_login_id in claims.
				const newTokenResult = await user.getIdTokenResult(true);
				if (!newTokenResult || !newTokenResult.claims.jw_login_id) {
					throw new Error('internal registration failed');
				}

				await sendEmailVerification(user, {
					url: location.toString(),
				});
				setAuthData({
					connected_accounts: user.providerData.map((p: LoginProviderAccount) => {
						return {
							email: p!.email,
							login_provider: LoginProviderByFirebaseProviderId[p!.providerId],
						};
					}),
					connected_providers: [],
					device_id: jwId.value!,
					login_id: newTokenResult.claims.jw_login_id,
					token: newTokenResult.token,
					loginProvider: LoginProviderByFirebaseProviderId[
						tokenResult.signInProvider as string
					] as LoginProvider,
				});

				await loadSettings();
				const constantStore = useConstantStore();

				await saveSettings({ providers: constantStore.preselectedProviders });
				await loadInfo();

				if (info.value?.email) await saveEmailHash(info.value.email);

				if (premiumPurchaseFlowState.value == PremiumPurchaseFlowState.AUTH_STARTED) {
					// set state to continue purchase flow without user interaction
					setPremiumPurchaseFlowState(PremiumPurchaseFlowState.AUTH_SUCCESS);
				}
			} catch (err) {
				throw err;
			} finally {
				setLoginProgress(false);
			}
		}

		function setPremiumPurchaseFlowState(value: PremiumPurchaseFlowState | null) {
			premiumPurchaseFlowState.value = value;
		}

		async function loadInfo() {
			if (!accessToken.value) {
				info.value = {};
				accountCreationTime.value = null;
				return;
			}

			const user = await currentUser();

			const userInfo = {
				name: user?.displayName || '',
				email: user?.email || '',
				loginProvider: info.value.loginProvider,
				emailVerified: user?.emailVerified,
				creationTime: user?.metadata?.creationTime,
				photoURL: user?.photoURL || '',
			};
			info.value = userInfo;
			accountCreationTime.value = user?.metadata?.creationTime;
		}

		function setAuthData(authData: AuthResponse) {
			if (authData.connected_accounts) {
				const providers: LoginProvider[] = [];
				for (let i = 0; i < authData.connected_accounts.length; i++) {
					// @ts-ignore
					const { email, login_provider, is_entry_point } = authData.connected_accounts[i];

					if (is_entry_point) {
						info.value.email = email;

						info.value.loginProvider = login_provider;
					}
					providers.push(login_provider);
				}

				loggedInProviders.value = providers;
			} else {
				if (authData.connected_providers.length === 1) {
					info.value.loginProvider = authData.connected_providers[0];
				}

				loggedInProviders.value = authData.connected_providers;
			}
			info.value.loginProvider = authData.loginProvider;

			jwLoginId.value = authData.login_id;
			accessToken.value = authData.token;

			if (loggedInProviders.value.length > 0) {
				saveOnboardingStatus(OnboardingStatus.USER);
			}
		}

		function setLoginProgress(value: boolean) {
			if (!value) useUserStats().refetchUserStats();
			loginProgress.value = value;
		}

		async function signInInternally() {
			setLoginProgress(true);

			const user = await currentUser();

			if (!user) {
				setLoginProgress(false);
				throw new Error('no firebase user');
			}
			try {
				const tokenResult = await user.getIdTokenResult(true);
				if (!tokenResult || !tokenResult.claims.jw_login_id) {
					throw new Error('need JW login ID for signing in internally');
				}

				await getAxios().post(
					`${JW_CONFIG.IDENTITY_URL}/sign_in`,
					{},
					{
						headers: {
							Authorization: 'Bearer ' + tokenResult.token,
							// @ts-ignore
							['DEVICE-ID']: jwId.value,
						},
					}
				);

				setAuthData({
					connected_accounts: user.providerData.map((p: LoginProviderAccount) => {
						return {
							email: p!.email,
							login_provider: LoginProviderByFirebaseProviderId[p!.providerId],
						};
					}),
					connected_providers: [],
					device_id: jwId.value!,
					login_id: tokenResult.claims.jw_login_id,
					token: tokenResult.token,
					loginProvider: LoginProviderByFirebaseProviderId[
						tokenResult.signInProvider as string
					] as LoginProvider,
				});

				// update settings
				await loadSettings();

				// only MOBILE

				await updateUserPremiumStatus();
				await loadInfo();

				if (info.value?.email) await saveEmailHash(info.value.email);

				// if sign-in/up was part of premium purchase flow
				if (premiumPurchaseFlowState.value == PremiumPurchaseFlowState.AUTH_STARTED) {
					if (isPremium.value) {
						// user already has Premium, abort purchase flow
						setPremiumPurchaseFlowState(null);
					} else {
						// set state to continue purchase flow without user interaction
						setPremiumPurchaseFlowState(PremiumPurchaseFlowState.AUTH_SUCCESS);
					}
				}
			} catch (err) {
				throw err;
			} finally {
				setLoginProgress(false);
			}
		}

		async function sendCode(code: string) {
			if (accessToken.value) {
				const headers = await getAuthHeaders(accessToken.value);
				return getAxios().post(`${JW_CONFIG.USER_URL}/tv/connect`, { code }, { headers });
			}
		}
		// @deprecated: use saveDPSConsents for newer consent management
		function saveConsents(consents: Record<Consent, boolean>) {
			saveSettings({ jw_consents: consents });
			TrackingHelper.trackConsent(consents);
			TrackingHelper.trackEvent('consent', {
				action: 'saved',
				nonInteraction: true,
			});
			saveConsentsHelper(consents);
			console.log('@todo replace with UsercentricsHelper.saveConsents(consents)');
		}

		/**
		 * we only locally save DPS consents that either:
		 * - have been answered explicitly
		 * - are true (usually being 'essential')
		 *
		 * note: it's possible that `usercentricsDpsConsents` has newer DPS that the local DPS (usercentrics-helper@LocalDataProcessingServices) don't have.
		 * in the getters `dpsConsents` we ignore DPS (either from UC or local) that aren't present in both sets.
		 */
		async function saveDPSConsents(usercentricsDpsConsents: DataProcessingServiceConsent[]) {
			const explicitConsents = usercentricsDpsConsents.filter(consent => consent.hasAnswered);
			const uc_consents = explicitConsents.map(item => ({ id: item.id, consent: item.status }));

			// we want to prevent saving the uc_consents if they haven't changed.
			const getStringifiedConsent = (consents: UserSettings['uc_consents']) =>
				JSON.stringify([...(consents || [])].sort((a, b) => b.id.localeCompare(a.id)));

			if (getStringifiedConsent(settings.value.uc_consents) !== getStringifiedConsent(uc_consents)) {
				saveSettings({ uc_consents });
				// Store the GDPR Consent TC string if it has changed
				setTCString(await storeTCString());
			}
		}

		function setLoginProviders(providers: LoginProvider[]) {
			loggedInProviders.value = providers;
		}

		function updateUserInfo(userInfoPayload: UpdateUserInfoPayload) {
			info.value = {
				...info.value,
				...userInfoPayload,
			};
		}

		async function updateUserPremiumStatus() {
			const { newPremiumStatus, subscriptionId } = await updatePremiumStatus(jwLoginId.value);
			await setPremiumStatus(newPremiumStatus);
			setSubscriptionInfo(subscriptionId ?? null);
		}

		async function deleteAccountPermanently() {
			const user = await currentUser();
			if (!user) throw 'not logged in to Firebase';

			try {
				await deleteUser(user);
				await logout();
			} catch (e) {
				const err: any = e;
				if (err.code !== 'auth/requires-recent-login') {
					const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
					captureMessageForSentry(
						'Error deleting user account:',
						{ error: err, where: '[user.store.ts] deleteAccountPermanently' },
						'error'
					);
				}
				throw e;
			}
		}

		function savePersistedOverriddenLanguage(language: string) {
			persistedOverriddenLanguage.value = language;
		}

		function saveSearchTerm(searchTerm: string) {
			if (searchTerm.trim().length > 0) {
				if (!searches.value.recentSearchTerms.includes(searchTerm)) {
					// recentSearchTerms = [searchTerm, ...recentSearchTerms];
					searches.value.recentSearchTerms = [searchTerm].concat(searches.value.recentSearchTerms);
				} else {
					const index = searches.value.recentSearchTerms.findIndex(term => term === searchTerm);
					const term = searches.value.recentSearchTerms[index];
					searches.value.recentSearchTerms.splice(0, 0, term);
					searches.value.recentSearchTerms.splice(index + 1, 1);
				}

				// limit stored search terms to 10 to avoid store explosion
				searches.value.recentSearchTerms = searches.value.recentSearchTerms.slice(0, RECENT_SEARCH_TERMS_COUNT);
			}
		}

		function saveVisitedTitleId(visitedTitleId: string) {
			const containsTitle: boolean = searches.value.recentlyVisitedTitleIds.some(
				(elem: string) => elem === visitedTitleId
			);

			if (!containsTitle) {
				searches.value.recentlyVisitedTitleIds = [visitedTitleId].concat(
					searches.value.recentlyVisitedTitleIds
				);
			} else {
				const index = searches.value.recentlyVisitedTitleIds.findIndex(titleId => titleId === visitedTitleId);
				const title = searches.value.recentlyVisitedTitleIds[index];
				searches.value.recentlyVisitedTitleIds.splice(0, 0, title);
				searches.value.recentlyVisitedTitleIds.splice(index + 1, 1);
			}

			// limit stored search terms to 10 to avoid store explosion
			searches.value.recentlyVisitedTitleIds = searches.value.recentlyVisitedTitleIds.slice(
				0,
				RECENT_SEARCH_TERMS_COUNT
			);
		}

		function clearSearchHistory() {
			searches.value.recentSearchTerms = [];
			searches.value.recentlyVisitedTitleIds = [];
		}

		function hideFlyout(flyoutName: VisibleFlyoutType | string) {
			visibleFlyouts.value[`${flyoutName}`] = false;
		}

		function setPremiumCancellationStatus(value: boolean) {
			hasCancelledPremium.value = value;
		}

		function setTimeZone(value: string) {
			timeZone.value = value;
		}

		function setIsVerificationEmailSent(isSent: boolean) {
			isVerificationEmailSent.value = isSent;
		}

		function setLastTimeMobileAppBannerWasClosedTS(timeStamp: number) {
			lastTimeMobileAppBannerWasClosedTS.value = timeStamp;
		}

		function setLastTimeListOnboardingWasShownTS(timeStamp: number) {
			lastTimeListsOnboardingWasShownTS.value = timeStamp;
		}

		async function updateUserVisitStatus() {
			if (isUserFirstVisit.value === true) {
				isUserFirstVisit.value = null;
			} else if (isUserFirstVisit.value === null) {
				isUserFirstVisit.value = false;
			}
		}

		function setPermanentAudiencesSubgenres(audiences: string[]) {
			permanentAudiencesSubgenres.value = audiences;
			TrackingHelper.refreshPermanentAudiencesSubgenres(audiences);
		}

		function resetPermanentAudiencesSubgenres() {
			permanentAudiencesSubgenres.value = [];
		}

		function setUserRole(role: UserRole) {
			userRole.value = role;
		}

		function toggleShowOldTitlePage() {
			showOldTitlePageState.value = !showOldTitlePageState.value;
		}

		function setUsercentricsDPS(services: { id: string; name: string }[]) {
			usercentricsDPS.value = services;
		}

		function setDiscoverySettings(settings: DiscoverySettings) {
			discoverySettings.value = settings;
		}

		function setPersistedWebLocale(webLocale: WebLocale) {
			persistedWebLocale.value = webLocale;
		}

		function saveOnboardingStatus(status: OnboardingStatus) {
			onboardingStatus.value = status;
		}

		function setTCString(tcString: string | null) {
			if (tcString == null) return;
			uc_tcf.value = tcString;
		}

		watch(
			() => languageStore.webLocale,
			(webLocale, oldWebLocale) => {
				if (webLocale === oldWebLocale) return;
				loadSettings();
			},
			{ immediate: false }
		);

		watch(accessToken, async token => {
			if (token == null) return;

			await loadSettings();
			const webLocale = languageStore.detectedWebLocale || languageStore.webLocale;
			const url = routingStore.activeRoute?.fullPath;
			saveOnboardingStatus(getOnboardingStatus(webLocale, isLoggedIn.value, url));
		});

		return {
			hasLoadedSettings,

			//#region state
			accessToken,
			accountCreationTime,
			discoverySettings,
			enableProvidersFilter,
			hasCancelledPremium,
			hideSeenDislikedTitles,
			info,
			isPremium,
			profileIsComplete,
			isVerificationEmailSent,
			jwId,
			jwLoginId,
			lastUsedLoginProvider,
			loggedInProviders,
			loginProgress,
			onboardingStatus,
			persistedOverriddenLanguage,
			persistedWebLocale,
			permanentAudiencesSubgenres,
			experimentsLoaded,
			preferredExperiments,
			preferences,
			profileInfo,
			newBuyboxFilterType,
			buyboxFilterTypeOptimistic,
			newBuyboxView,
			newBuyboxTooltipAcknowledged,
			loggedInNewBuyBoxView,
			displayHomeSportsModule,
			premiumPurchaseFlowState,
			searches,
			seenSurveys,
			settings,
			settingsProvider,
			storageMigratedRevision,
			lastTimeMobileAppBannerWasClosedTS,
			lastTimeListsOnboardingWasShownTS,
			subscriptionInfoState,
			timeZone,
			visibleFlyouts,
			usercentricsDPS,
			userRole,

			isUserFirstVisit,
			showOldTitlePageState,
			acquiredExperiments,
			_preferredExperiments,
			activatedExperiments,
			uc_tcf,
			optedOutConsent,
			// #endregion

			//#region getters

			userInfo,
			buyboxPresentationType,
			isLoggedIn,
			newBuyboxTooltipAcknowledgedGetter,
			bestEffortId,
			consents,
			dpsConsents,
			strictlyAllConsentsGranted,
			userSearches,
			isUserOnboarded,
			isHideSeenDislikedTitlesEnabled,
			isAdmin,
			isEditor,
			isAccountVerified,
			isProUser,
			subscriptionInfo,
			loggedInNewBuyboxView,
			showOldTitlePage,
			userExperimentsInfo,
			tcString,
			showFreemium,
			//#endregion

			//#region actions
			setAcquiredExperiments,
			setNewPreferredExperiments,
			setActivatedExperiments,
			registerActivatedExperiment,
			addActivatedExperiment,
			setJwId,
			fetchJwId,
			setBuyboxPresentationType,
			setNewBuyboxFilterType,
			setBuyboxFilterTypeOptimistic,
			setNewBuyboxView,
			setNewBuyboxTooltipAcknowledged,
			hideHomeSportsModule,
			setProfileIsComplete,
			verifyRegistration,
			setAuthToken,
			setPremiumStatus,
			setProfileInfo,
			setSubscriptionInfo,
			setSettings,
			loadSettings,
			saveSettings,
			logout,
			refreshToken,
			setLoginProgress,
			setAuthData,
			loadInfo,
			registerInternally,
			signInInternally,
			sendCode,
			saveConsents,
			saveDPSConsents,
			setLoginProviders,
			updateUserInfo,
			deleteAccountPermanently,
			savePersistedOverriddenLanguage,
			saveSearchTerm,
			saveVisitedTitleId,
			clearSearchHistory,
			hideFlyout,
			setPremiumCancellationStatus,
			setTimeZone,
			setIsVerificationEmailSent,
			setLastTimeMobileAppBannerWasClosedTS,
			setLastTimeListOnboardingWasShownTS,
			updateUserVisitStatus,
			setPermanentAudiencesSubgenres,
			resetPermanentAudiencesSubgenres,
			setUserRole,
			toggleShowOldTitlePage,
			setUsercentricsDPS,
			setDiscoverySettings,
			setPersistedWebLocale,
			setHideSeenDislikedTitles,
			saveOnboardingStatus,
			isFlyoutVisible,
			setTCString,
			setPremiumPurchaseFlowState,
			setOptedOutConsent,
			updateUserPremiumStatus,
			//#endregion

			// OLD BUYBOX EXP
			buyboxExpView,
			setBuyboxExpView,
			buyboxViewExpTooltipAcknowledged,
			setBuyboxViewExpTooltipAcknowledged,
			// OLD BUYBOX EXP
		};
	},
	{
		persist: [
			{
				storage: {
					getItem(key) {
						if (!localStorage) return null;
						return localStorage.getItem(`${STORE_NAMESPACE}/${key}`);
					},
					setItem(key, value) {
						if (!localStorage) return;
						localStorage.setItem(`${STORE_NAMESPACE}/${key}`, value);
					},
				},
			},
			{
				storage: cookieStorage,
				pick: [
					'accessToken',
					'hideSeenDislikedTitles',
					'isPremium',
					'userRole',
					'jwId',
					'jwLoginId',
					'lastUsedLoginProvider',
					'location',
					'loggedInProviders',
					'onboardingStatus',
					'persistedOverriddenLanguage',
					'persistedWebLocale',
					'preferences',
					'settings',
					'visibleFlyouts',
					'newBuyboxFilterType',
					'newBuyboxView',
					'newBuyboxTooltipAcknowledged',
					'loggedInNewBuyBoxView',
					'buyboxExpView',
					'accountCreationTime',
					'showOldTitlePage',
				],
			},
		],
	}
);
